import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, FieldArray, Form } from 'formik';
import { ModalError } from '@rsos/components/capstone/RolesShared/Common/ModalInserts';
import { Box, Text, Button, Modal } from '@rsos/components/capstone/base';
import {
  ModalBody,
  ModalHeader,
  ModalFooter,
} from '@rsos/components/capstone/base/Modal/ModalComponents';
import { selectUserOrgByApp, updatePermissionRoles } from '@rsos/sinatra';
import { notifySuccess, notifyError } from '@rsos/utils/toastUtils';
import { Checkbox } from '../../common.styles';
import {
  ScrollableContainer,
  RowContainer,
} from './RemoveDataSourceModal.styles';

const initialRequestStatus = {
  loading: false,
  idle: true,
  error: null,
};

const RemoveDataSourceModal = ({ isOpened, onClose, dataSource }) => {
  const [requestStatus, setRequestStatus] = useState(initialRequestStatus);

  const sinatra = useSelector(state => state.sinatra);
  const dispatch = useDispatch();
  const { application } = sinatra.user.currentRole;
  const org = selectUserOrgByApp(sinatra, application);
  const [isSelectAll, setIsSelectAll] = useState(false);

  const handleCheckbox = (
    event,
    index,
    value,
    checkboxValues,
    arrayHelpers,
  ) => {
    setIsSelectAll(false);
    if (event.target.checked) {
      arrayHelpers.push(value);
    } else {
      arrayHelpers.remove(checkboxValues.indexOf(value));
    }
  };

  const handleClose = handleReset => {
    handleReset();
    setRequestStatus(initialRequestStatus);
    onClose();
  };
  const handleUpdatePermission = roleIds => {
    setRequestStatus({ loading: true, idle: false, error: null });

    // NOTE: roleIds is an array of Ids to be removed consistent with the UI. the API needs the opposite: an array of ids to keep. Hence the filter is needed.
    const inversedRoleIds = dataSource.roles
      .filter(r => !roleIds.includes(r.id))
      .map(i => i.id);
    const payload = {
      role_ids: inversedRoleIds,
    };
    dispatch(
      updatePermissionRoles(org.id, application, payload, dataSource.name),
    )
      .then(() => {
        setRequestStatus({ loading: false, idle: true, error: null });

        notifySuccess('Updated');
        onClose();
      })
      .catch(e => {
        const error =
          e.error?.response?.data?.detail ||
          e.error?.message ||
          'There was an error';
        notifyError(error);
        setRequestStatus({ loading: false, idle: false, error: error });
        notifyError(error.response.data.detail);
      });
  };
  const handleTryAgain = () => {
    setRequestStatus(initialRequestStatus);
  };

  const handleSelectAll = setValues => {
    setIsSelectAll(true);
    setValues({ roleIds: dataSource.roles.map(r => r.id) });
  };

  return (
    <Formik
      initialValues={{ roleIds: [] }}
      enableReinitialize
      onSubmit={async values => {
        handleUpdatePermission(values.roleIds);
      }}
    >
      {({ values, handleReset, setValues }) => (
        <Modal
          isOpened={isOpened}
          onClose={() => handleClose(handleReset)}
          width={404}
          id="modal-remove-data-source"
        >
          {requestStatus.error ? (
            <ModalError
              onClick={handleTryAgain}
              error={requestStatus.error}
              onClose={() => handleClose(handleReset)}
            />
          ) : (
            <>
              <ModalHeader onClose={() => handleClose(handleReset)}>
                <Box width="100%">
                  <Text size="xlarge" color="primaryText" mb={12}>
                    Remove Roles from Data Source
                  </Text>
                  <Text size="small" color="secondaryText">
                    Remove the following roles from accessing the data source,{' '}
                    {dataSource.display_name}
                  </Text>
                </Box>
              </ModalHeader>
              <ModalBody>
                <Form id="removeDataSource">
                  <ScrollableContainer>
                    <FieldArray name="roleIds">
                      {arrayHelpers => (
                        <div
                          id="remove-dataSource-checkbox-group"
                          aria-labelledby="remove-dataSource-checkbox-group"
                          role="group"
                        >
                          <RowContainer>
                            <Checkbox
                              type="checkbox"
                              checked={isSelectAll}
                              disabled={isSelectAll}
                              onChange={() => handleSelectAll(setValues)}
                            />
                            <Text size="normal" color="secondaryText">
                              Select All
                            </Text>
                          </RowContainer>
                          {dataSource.roles.map((role, index) => (
                            <RowContainer key={role.id}>
                              <Checkbox
                                type="checkbox"
                                data-name={role.name}
                                name={role.name}
                                value={role.id}
                                checked={
                                  values?.roleIds?.find(r => {
                                    return r === role.id;
                                  }) || false
                                }
                                onChange={e =>
                                  handleCheckbox(
                                    e,
                                    index,
                                    role.id,
                                    values.roleIds,
                                    arrayHelpers,
                                  )
                                }
                              />{' '}
                              <Text size="normal" color="secondaryText">
                                {role.name}
                              </Text>
                            </RowContainer>
                          ))}
                        </div>
                      )}
                    </FieldArray>
                  </ScrollableContainer>
                </Form>
              </ModalBody>
              <ModalFooter flow={12}>
                <Button
                  onClick={onClose}
                  grow
                  noMargin
                  btnType="tertiary"
                  form="removeDataSource"
                  type="button"
                >
                  Cancel
                </Button>
                <Button
                  grow
                  noMargin
                  btnType="primary"
                  type="submit"
                  form="removeDataSource"
                  isLoadingProp={requestStatus.loading}
                >
                  Remove
                </Button>
              </ModalFooter>
            </>
          )}
        </Modal>
      )}
    </Formik>
  );
};

export default RemoveDataSourceModal;
